import React from "react";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import ShopType from "@/enum/ShopType";
import OrderStatus from "@/enum/OrderStatus";
import OrderItemImage from "../../../../../../../components/OrderItemImage";
import { observer } from "mobx-react-lite";
import classnames from "classnames";
import OrderItemStatusLabel from "../../../../../../../components/OrderItemImage/OrderItemStatusLabel";

interface OrderItemDisplayWithPicProps {
    orderItem: OrderItemDTO;
    shopType: ShopType;
    orderStatus?: OrderStatus;
    hasAbnormal: boolean;
    showTitle: boolean;
    showSimpleTitle: boolean;
    showExternalId: boolean;
    showSkuProperties: boolean;
    picSize: number;
}

/**
 * 勾选显示图片 则使用此组件
 */
const OrderItemDisplayWithPic: React.FC<OrderItemDisplayWithPicProps> = observer((props) => {
    const {
        orderItem,
        shopType,
        orderStatus,
        hasAbnormal,
        showTitle,
        showSimpleTitle,
        showExternalId,
        showSkuProperties,
        picSize,
    } = props;

    const simpleTitle = orderItem.simpleTitle;
    const title = orderItem.title;
    const externalId = orderItem.skuExternalId || orderItem.productExternalId;
    const skuProperties = orderItem.localSkuProperties || orderItem.skuProperties;

    const totalElements: React.ReactNode[] = [];

    if (showTitle && showSimpleTitle) {
        if (simpleTitle) {
            totalElements.push(
                <span key="showTitleAndSimpleTitle" className="break-spaces">
                    {title}({simpleTitle})
                </span>,
            );
        } else {
            totalElements.push(
                <span key="showTitle" className="break-spaces">
                    {title}
                </span>,
            );
        }
    } else if (showTitle) {
        totalElements.push(
            <span key="showTitle" className="break-spaces">
                {title}
            </span>,
        );
    } else if (showSimpleTitle && simpleTitle) {
        totalElements.push(
            <span key="showSimpleTitle" className="break-spaces">
                {simpleTitle}
            </span>,
        );
    }

    if (showExternalId && externalId && showSkuProperties && skuProperties) {
        if (totalElements.length) {
            totalElements.push(<br key="br" />);
        }
        totalElements.push(
            <span key="showExternalIdAndSKuProperties" className="text-muted break-spaces">
                {externalId} &nbsp; ({skuProperties})
            </span>,
        );
    } else if (showExternalId && externalId) {
        if (totalElements.length) {
            totalElements.push(
                ...[
                    <br key="br" />,
                    <span key="showExternalId" className="text-muted">
                        {externalId}
                    </span>,
                ],
            );
        } else {
            totalElements.push(
                <span key="showExternalId" className="text-muted">
                    {externalId}
                </span>,
            );
        }
    } else if (showSkuProperties && skuProperties) {
        if (totalElements.length) {
            totalElements.push(
                ...[
                    <br key="br" />,
                    <span key="showSkuProperties" className="text-muted break-spaces">
                        {skuProperties}
                    </span>,
                ],
            );
        } else {
            totalElements.push(
                <span key="showSkuProperties" className="text-muted break-spaces">
                    {skuProperties}
                </span>,
            );
        }
    }

    totalElements.push(
        <span
            className={classnames(
                "mr10",
                orderItem.quantity >= 2 ? "high-light-order-item-quantity" : "order-item-quantity",
            )}
            key="quantity"
        >
            × {orderItem.quantity}
            {orderItem.canceledQuantity > 0 ? (
                <span className="text-danger font-bold fs11" key="canceledQuantity">
                    （取消数量：{orderItem.canceledQuantity}）
                </span>
            ) : null}
        </span>,
    );

    if (orderStatus !== orderItem.status && !orderItem.picture) {
        totalElements.push(<OrderItemStatusLabel status={orderItem.status} />);
    }

    return (
        <div className="order-item-pic-props">
            {orderItem.picture ? (
                <OrderItemImage
                    orderStatus={orderStatus}
                    orderItem={orderItem}
                    shopType={shopType}
                    showWarehouseWarning={true}
                    hasAbnormal={hasAbnormal}
                    size={picSize}
                />
            ) : null}

            <div className="orderItem-info">{totalElements}</div>
        </div>
    );
});

export default OrderItemDisplayWithPic;
